import { NavLink, Outlet } from 'react-router-dom';
import type { CSSProperties } from 'react';
import { useAuth } from '../lib/auth/AuthProvider';
import { useFirm } from '../lib/firm/FirmProvider';
import './AppShell.css';

const BASE_NAV: ReadonlyArray<{ to: string; label: string; end?: boolean }> = [
  { to: '/', label: 'Overview', end: true },
  { to: '/clients', label: 'Clients' },
  { to: '/attestations', label: 'Attestations' },
  { to: '/recoveries', label: 'Lost devices' },
  { to: '/events', label: 'Upcoming' },
  { to: '/invites', label: 'Invite' },
  { to: '/audit', label: 'Audit log' },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const { firm } = useFirm();

  // The firm's accent tints the brand touchpoints (active nav, firm name) via a
  // single custom property. It intentionally does NOT override the house gold
  // everywhere — the dark surfaces are tuned for it — only the deliberate brand
  // spots, so a firm's navy or forest-green never disappears into the ground.
  const shellStyle = firm?.brand_accent
    ? ({ '--brand-accent': firm.brand_accent } as CSSProperties)
    : undefined;

  // Both of these are administrator-only. `can_manage_branding` is the same
  // is_primary signal the succession endpoints gate on, so one flag drives both
  // rather than inventing a second notion of "admin" that could drift from it.
  const nav = firm?.can_manage_branding
    ? [...BASE_NAV, { to: '/team', label: 'Advisors' }, { to: '/branding', label: 'Branding' }]
    : BASE_NAV;

  return (
    <div className="shell" style={shellStyle}>
      <aside className="shell__nav" aria-label="Advisor navigation">
        <div className="shell__brand">
          {firm !== null && firm.logo_url !== null ? (
            <>
              <img className="shell__logo" src={firm.logo_url} alt={firm.name} />
              <p className="shell__poweredBy">on Legacy Architect</p>
            </>
          ) : firm !== null ? (
            <>
              <h1 className="shell__brandName shell__brandName--firm">{firm.name}</h1>
              <p className="shell__poweredBy">on Legacy Architect</p>
            </>
          ) : (
            <>
              <span className="la-label">Legacy Architect</span>
              <h1 className="shell__brandName">Advisor</h1>
            </>
          )}
        </div>

        <nav>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `shell__link${isActive ? ' shell__link--active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="shell__foot">
          {user !== null && (
            <>
              <p className="shell__email">{user.email}</p>
              {firm?.can_manage_branding && (
                <p className="la-label shell__role">Firm admin — firm-wide view</p>
              )}
            </>
          )}
          <button type="button" className="la-btn-secondary shell__signout" onClick={logout}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="shell__main">
        <Outlet />
      </main>
    </div>
  );
}
